import { AppBar } from "@remo-co/ui-core/src/components/AppBar";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { RemoLogo } from "@remo-co/ui-core/src/components/RemoLogo";
import { Toolbar } from "@remo-co/ui-core/src/components/Toolbar";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { Create } from "@remo-co/ui-core/src/icons/Create";
import { FolderShared } from "@remo-co/ui-core/src/icons/FolderShared";
import { useI18n } from "i18n";
import { useAppDispatch } from "store/hooks";
import { showPopup as showSwitchCompaniesPopup } from "modules/switchCompanies/redux";
import { AppCircleLogo } from "modules/app/branding";
import {
  selectIsBroadcasting,
  selectIsInBroadcast,
} from "modules/broadcast/redux/selectors";
import getWhitelabelledLogo from "modules/company/utils/getWhitelabelledLogo";
import { selectIsMobile } from "modules/deviceInfo";
import { useIsInEvent } from "modules/event";
import {
  selectCurrentEvent,
  selectCurrentEventCode,
  selectCurrentEventCompanyLogoUrl,
  selectCurrentEventId,
  selectCurrentEventIsLobbyActive,
  selectEventCompany,
  selectIsEventManager,
} from "modules/event/selectors";
import { EventAgendaButton } from "modules/eventAgendaPublic/components/EventAgendaButton";
import { selectIsLobbyBannerOpen } from "modules/lobby/redux/selectors";
import MapNavigation from "modules/mapNavigation/MapNavigation";
import { useShuffleAttendees } from "modules/shuffleAttendees/hooks/useShuffleAttendees";
import { useMapView } from "modules/svgMapView/templateParts/MapViewContext";
import { ShareEventButton } from "modules/theater/banner/components/ShareEventButton";
import { MapBanner } from "modules/theater/banner/MapBanner";
import { UserPresence } from "modules/theater/banner/userPresence";
import { DisplayLayout } from "modules/audioVideo";
import { selectShowTimer } from "modules/timer/redux/selectors";
import ProfileMenu from "modules/userProfileMenu";
import { memo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { selectLayout } from "modules/audioVideo/redux/selectors";
import {
  selectMyCompany,
  selectMultipleCompanyView,
} from "modules/company/redux/selectors";
import { LiveUserSessionsSubscriber } from "modules/liveUserSessions/components/LiveUserSessionsSubscriber";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { selectCurrentEventDirectories } from "modules/eventDirectory/redux/selectors";
import {
  selectEnableAgencyWhiteLabelling,
  selectWhiteLabelLogoURL,
} from "modules/settings/redux/selectors";
import { selectIsCountdownBannerVisible } from "modules/enforceTableAssignment/redux";
import EventDirectorySwitch from "../EventDirectorySwitch";
import FloorSwitch from "../FloorSwitch";
import { useStyles } from "./styles";
import { ContactSupport } from "../ContactSupport";

const TopBar = () => {
  const dispatch = useAppDispatch();
  const { panZoom } = useMapView();
  const { t } = useI18n(["common", "event"]);
  const showTimerPanel = useSelector(selectShowTimer);
  const eventDirectories = useSelector(selectCurrentEventDirectories);
  const layout = useSelector(selectLayout);
  const isTileView = layout === DisplayLayout.Tile;
  const isFocusView = layout === DisplayLayout.Focus;
  const isEventManager = useSelector(selectIsEventManager);
  const isInEvent = useIsInEvent();
  const isBroadcasting = useSelector(selectIsBroadcasting);
  const isLobbyBannerOpen = useSelector(selectIsLobbyBannerOpen);
  const currentEvent = useSelector(selectCurrentEvent);
  const isLobbyActive = useSelector(selectCurrentEventIsLobbyActive);
  const { showShuffleAttendeesBanner } = useShuffleAttendees();
  const isMobileView = useSelector(selectIsMobile);
  const enableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const eventCompanyLogoURL = useSelector(selectCurrentEventCompanyLogoUrl);
  const whiteLabelLogoUrl = useSelector(selectWhiteLabelLogoURL);
  const isEnforceCountdownBannerVisible = useSelector(
    selectIsCountdownBannerVisible,
  );
  const isInBroadcast = useSelector(selectIsInBroadcast);
  const styles = useStyles({ showTimerPanel, eventDirectories });
  const showLobbyBanner = isLobbyActive && isLobbyBannerOpen && !isMobileView;
  const eventCompany = useSelector(selectEventCompany) ?? undefined;
  const company = useSelector(selectMyCompany);
  const multipleCompanyView = useSelector(selectMultipleCompanyView);
  const eventCompanyData =
    typeof eventCompany !== "string" ? eventCompany ?? undefined : undefined;
  const { push } = useHistory();
  const eventId = useSelector(selectCurrentEventId);
  const eventCode = useSelector(selectCurrentEventCode);

  const whiteLabelledLogoURL = getWhitelabelledLogo(
    eventCompanyData,
    currentEvent?.eventBrandingLogoURL,
  );
  // show agency white label logo if enabled and there's either a white label logo or company logo uploaded
  const agencyWhiteLabellingLogoEnabled =
    enableAgencyWhiteLabelling && (whiteLabelLogoUrl || eventCompanyLogoURL);

  const showButtons = isInEvent && !isMobileView;
  const showEditEventButton =
    showButtons && isEventManager && eventCompanyData?.id === company?.id;
  const showSwitchCompanyButton =
    showButtons &&
    !showEditEventButton && // deliberately hide this if we're showing the edit event button
    multipleCompanyView; // only for users who are part of multiple companies

  return (
    <AppBar className={styles.appBar} position="relative">
      <Toolbar disableGutters className={styles.topBar}>
        <Container flex justifyContent="flex-start">
          {isEventManager ? (
            <UserPresence />
          ) : (
            <Container
              flex
              justifyContent="center"
              alignItems="center"
              className={styles.logoContainer}
            >
              {whiteLabelledLogoURL || agencyWhiteLabellingLogoEnabled ? (
                <div className={styles.logo}>
                  <AppCircleLogo width={55} />
                </div>
              ) : (
                <RemoLogo
                  variant={isBroadcasting ? "presentation" : undefined}
                  size="sm"
                  className={styles.logo}
                />
              )}
            </Container>
          )}
          <div className={styles.mapBanner}>
            <MapBanner hasEventMap={Boolean(eventDirectories && isInEvent)} />
          </div>
        </Container>
        {!showShuffleAttendeesBanner &&
          !isEnforceCountdownBannerVisible &&
          !showLobbyBanner && (
            <Container flex fullWidth>
              {eventDirectories && isInEvent && (
                <EventDirectorySwitch eventDirectories={eventDirectories} />
              )}
              {!isBroadcasting && <FloorSwitch />}
              {!isMobileView && <EventAgendaButton />}
            </Container>
          )}
        <Container
          flex
          justifyContent="flex-end"
          className={styles.profileMenu}
        >
          <ShareEventButton />
          <ContactSupport />
          {panZoom && !isTileView && !isInBroadcast && !isFocusView && (
            <div className={styles.mapNavigation}>
              <MapNavigation panzoomInstance={panZoom} />
            </div>
          )}
          {showEditEventButton && (
            <Tooltip title={t("event:edit.event")} placement="bottom">
              <IconButton
                data-testid="edit-event-button"
                className={styles.editEventButton}
                onClick={() =>
                  push(`/e/${eventCode}/event/settings/${eventId}`)
                }
              >
                <Create />
              </IconButton>
            </Tooltip>
          )}
          {showSwitchCompanyButton && (
            <Tooltip
              title={t("menu:user.menu.change.company")}
              placement="bottom"
            >
              <IconButton
                data-testid="switch-company-button"
                className={styles.supportButton}
                onClick={() => dispatch(showSwitchCompaniesPopup())}
              >
                <FolderShared />
              </IconButton>
            </Tooltip>
          )}
          <ProfileMenu broadcast={isBroadcasting} />
        </Container>
      </Toolbar>
      <LiveUserSessionsSubscriber />
    </AppBar>
  );
};

export default memo(TopBar);
